import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  Cell,
  ZAxis,
} from 'recharts';
import type { ClassificationPageMetric } from '../lib/classificationVisuals';
import { ScatterPagePreviewTooltip } from './ScatterPagePreviewTooltip';
import { marginToColor } from '../lib/classificationMarginColor';
import { CONFIDENCE_BANDS } from '../lib/confidenceBand';

const pct = (v: number) => `${Math.round(v * 100)}%`;

// Bucket edges follow the shared band cutoffs so the histogram colors agree
// with the badge / table banding everywhere else.
const BINS = [
  { label: '<50%', min: 0, max: 0.5, fill: '#ef4444' },
  { label: `50–${pct(CONFIDENCE_BANDS.medium)}`, min: 0.5, max: CONFIDENCE_BANDS.medium, fill: '#f59e0b' },
  {
    label: `${pct(CONFIDENCE_BANDS.medium)}–${pct(CONFIDENCE_BANDS.high)}`,
    min: CONFIDENCE_BANDS.medium,
    max: CONFIDENCE_BANDS.high,
    fill: '#facc15',
  },
  { label: `${pct(CONFIDENCE_BANDS.high)}–100%`, min: CONFIDENCE_BANDS.high, max: Infinity, fill: '#22c55e' },
];

export function ClassificationConfidenceHistogram({
  pages,
  representativePages,
  onPageClick,
}: {
  pages: ClassificationPageMetric[];
  representativePages: ClassificationPageMetric[];
  onPageClick?: (pageNumber: number) => void;
}) {
  const histogramData = useMemo(() => {
    const counts = BINS.map(() => 0);
    for (const p of pages) {
      const idx = BINS.findIndex((b) => p.confidence >= b.min && p.confidence < b.max);
      counts[idx === -1 ? BINS.length - 1 : idx]++;
    }
    return BINS.map((bin, i) => ({ name: bin.label, count: counts[i], fill: bin.fill }));
  }, [pages]);

  const stripData = useMemo(() => {
    return representativePages.map((p) => ({
      pageId: p.pageId,
      confidence: +(p.confidence * 100).toFixed(1),
      jitter: ((p.pageNumber * 7 + 3) % 10) / 10,
      margin: p.margin,
      pageNumber: p.pageNumber,
    }));
  }, [representativePages]);

  if (!pages.length) return null;

  return (
    <div className="space-y-4">
      {/* Histogram */}
      <div>
        <h4 className="text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1">Confidence histogram</h4>
        <div className="h-32 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart accessibilityLayer={false} data={histogramData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12 }} formatter={(value) => [`${Number(value)} pages`, 'Count']} />
              <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                {histogramData.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Strip plot */}
      <div>
        <h4 className="text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1">
          Per-page confidence <span className="font-normal">(color = margin)</span>
        </h4>
        <div className="h-24 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart accessibilityLayer={false} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" />
              <XAxis
                dataKey="confidence"
                type="number"
                domain={[0, 100]}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${v}%`}
                label={{ value: 'Confidence', position: 'insideBottom', offset: -2, fontSize: 10 }}
              />
              <YAxis dataKey="jitter" type="number" domain={[0, 1]} hide />
              <ZAxis range={[30, 30]} />
              <Tooltip
                content={
                  <ScatterPagePreviewTooltip
                    pageIdKey="pageId"
                    stats={[
                      { key: 'confidence', label: 'Confidence', format: (v) => `${v}%` },
                      { key: 'margin', label: 'Margin', format: (v) => `${((v as number) * 100).toFixed(1)}pp` },
                    ]}
                  />
                }
              />
              <Scatter data={stripData} fillOpacity={0.7} cursor={onPageClick ? 'pointer' : undefined}>
                {stripData.map((entry) => (
                  <Cell
                    key={entry.pageNumber}
                    fill={marginToColor(entry.margin)}
                    onClick={() => onPageClick?.(entry.pageNumber)}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
